import type { Disposable } from "@/shared/contracts/lifecycle";
import pauseIconSource from "bootstrap-icons/icons/pause-fill.svg?raw";
import { getMessages } from "@/i18n/runtime";
import { HoverIntentController } from "@/features/hoverIdle/core/hoverIntentController";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import {
  formatForContext,
  getCurrentNotation,
} from "@/features/hoverIdle/resources/numberFormat";
import { MOUSE_POINTER_ICON_SVG_SOURCE } from "@/shared/ui/mousePointerIcon";
import { MpSlotChargeController } from "@/features/hoverIdle/mpm/mpSlotChargeController";
import {
  resolveDirectMpTargetViewState,
  type DirectMpTargetViewState,
} from "@/features/hoverIdle/mpm/directMpTargetViewState";
import {
  createUpgradeTooltip,
  type UpgradeTooltipElements,
} from "@/features/hoverIdle/upgrades/upgradeTooltipView";
import type {
  UpgradeRepeatInitialIntervalSource,
  UpgradeRepeatSpeedMultiplierSource,
} from "@/features/hoverIdle/upgrades/upgradeRepeatTiming";
import type { AmplifierUpgradeGuardMs } from "@/features/hoverIdle/types";
import {
  createHovAmplifierUpgradeInteractionController,
  type HovAmplifierUpgradeInteractionController,
} from "./hovAmplifierUpgradeInteractionController";
import {
  formatHovAmplifierRemainingTime,
  resolveDominantVisualSegmentSource,
  type HovAmplifierColorKey,
  type HovAmplifierEntryId,
  type HovAmplifierEntrySnapshot,
  type HovAmplifierGaugeVisualSegment,
  type HovAmplifierSnapshot,
} from "./hovAmplifierModel";
import {
  computeAmplifierSnapshotUpgradeState,
  type AmplifierSnapshotUpgradeState,
} from "./hovAmplifierUpgradeState";

export interface HovAmplifierPanelViewOptions {
  readonly pointerModel?: HoverPointerModel;
  readonly onAmplifyEntryHoverChange?: (
    entryId: HovAmplifierEntryId,
    isHovering: boolean,
  ) => void;
  readonly onPurchaseHovAmplifierGainUpgrade?: (
    entryId: HovAmplifierEntryId,
  ) => boolean;
  readonly getAmplifierUpgradeGuardMs?: () => AmplifierUpgradeGuardMs;
  readonly onAmplifierUpgradeGuardMsChange?: (
    value: AmplifierUpgradeGuardMs,
  ) => void;
  readonly onAmplifierPanelMpAssign?: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierPanelMpRemove?: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierUpgradeMpAssign?: (entryId: HovAmplifierEntryId) => void;
  readonly onAmplifierUpgradeMpRemove?: (entryId: HovAmplifierEntryId) => void;
  readonly getHoverToggleDurationMs?: () => number;
  readonly upgradeRepeatInitialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
}

export interface HovAmplifierPanelMpState {
  readonly sectionUnlocked: boolean;
  readonly sectionEnabled: boolean;
  readonly availableMp: number;
  readonly assignedEntryIds: ReadonlySet<HovAmplifierEntryId>;
}

export interface HovAmplifierUpgradeMpState {
  readonly sectionUnlocked: boolean;
  readonly sectionEnabled: boolean;
  readonly availableMp: number;
  readonly assignedEntryIds: ReadonlySet<HovAmplifierEntryId>;
}

export interface HovAmplifierPanelUpdateOptions {
  readonly amplifierUnlocked?: boolean;
  readonly amplifierUpgradeGuardMs?: AmplifierUpgradeGuardMs;
  readonly hasSeenAvailableAmplifierUpgradeGuard?: boolean;
  readonly mpPlacement?: HovAmplifierPanelMpState;
  readonly upgradeMpPlacement?: HovAmplifierUpgradeMpState;
}

export class HovAmplifierPanelView implements Disposable {
  readonly root: HTMLDivElement;
  private readonly guardRoot: HTMLDivElement;
  private readonly guardButtons = new Map<
    AmplifierUpgradeGuardMs,
    HTMLButtonElement
  >();
  private readonly apValue: HTMLSpanElement;
  private readonly hgFormula: HTMLSpanElement;
  private readonly hgMultiplierValue: HTMLSpanElement;
  private readonly entriesRoot: HTMLDivElement;
  private readonly entryRows = new Map<
    HovAmplifierEntryId,
    HovAmplifierEntryRowView
  >();
  private readonly pointerModel: HoverPointerModel | null;
  private readonly onAmplifyEntryHoverChange:
    | ((entryId: HovAmplifierEntryId, isHovering: boolean) => void)
    | null;
  private readonly onPurchaseHovAmplifierGainUpgrade:
    | ((entryId: HovAmplifierEntryId) => boolean)
    | null;
  private readonly getAmplifierUpgradeGuardMs:
    | (() => AmplifierUpgradeGuardMs)
    | null;
  private readonly onAmplifierUpgradeGuardMsChange:
    | ((value: AmplifierUpgradeGuardMs) => void)
    | null;
  private readonly onAmplifierPanelMpAssign:
    | ((entryId: HovAmplifierEntryId) => void)
    | null;
  private readonly onAmplifierPanelMpRemove:
    | ((entryId: HovAmplifierEntryId) => void)
    | null;
  private readonly onAmplifierUpgradeMpAssign:
    | ((entryId: HovAmplifierEntryId) => void)
    | null;
  private readonly onAmplifierUpgradeMpRemove:
    | ((entryId: HovAmplifierEntryId) => void)
    | null;
  private readonly getHoverToggleDurationMs: (() => number) | null;
  private readonly upgradeRepeatInitialIntervalMs:
    | UpgradeRepeatInitialIntervalSource
    | undefined;
  private readonly speedMultiplier:
    | UpgradeRepeatSpeedMultiplierSource
    | undefined;
  private readonly cleanups: Array<() => void> = [];

  constructor(options: HovAmplifierPanelViewOptions = {}) {
    this.pointerModel = options.pointerModel ?? null;
    this.onAmplifyEntryHoverChange = options.onAmplifyEntryHoverChange ?? null;
    this.onPurchaseHovAmplifierGainUpgrade =
      options.onPurchaseHovAmplifierGainUpgrade ?? null;
    this.getAmplifierUpgradeGuardMs =
      options.getAmplifierUpgradeGuardMs ?? null;
    this.onAmplifierUpgradeGuardMsChange =
      options.onAmplifierUpgradeGuardMsChange ?? null;
    this.onAmplifierPanelMpAssign = options.onAmplifierPanelMpAssign ?? null;
    this.onAmplifierPanelMpRemove = options.onAmplifierPanelMpRemove ?? null;
    this.onAmplifierUpgradeMpAssign =
      options.onAmplifierUpgradeMpAssign ?? null;
    this.onAmplifierUpgradeMpRemove =
      options.onAmplifierUpgradeMpRemove ?? null;
    this.getHoverToggleDurationMs = options.getHoverToggleDurationMs ?? null;
    this.upgradeRepeatInitialIntervalMs =
      options.upgradeRepeatInitialIntervalMs;
    this.speedMultiplier = options.speedMultiplier;
    const messages = getMessages();
    this.root = document.createElement("div");
    this.root.className = "hover-idle-amplifier-panel";
    this.root.setAttribute("role", "region");
    this.root.setAttribute("aria-label", messages.hovView.region);
    this.root.hidden = true;

    const settingsLayer = document.createElement("div");
    settingsLayer.className =
      "hover-idle-game-surface-settings hover-idle-amplifier-settings";

    this.guardRoot = document.createElement("div");
    this.guardRoot.className =
      "hover-idle-game-surface-segmented-control hover-idle-amplifier-guard";
    this.guardRoot.dataset.amplifierUpgradeGuard = "true";
    this.guardRoot.setAttribute("role", "radiogroup");
    this.guardRoot.setAttribute(
      "aria-label",
      messages.hovView.amplifier.guard.label,
    );
    this.guardRoot.hidden = true;
    const guardLabel = document.createElement("span");
    guardLabel.className =
      "hover-idle-game-surface-segmented-control-label hover-idle-amplifier-guard-label";
    guardLabel.textContent = `${messages.hovView.amplifier.guard.label}:`;
    const guardOptions = document.createElement("div");
    guardOptions.className =
      "hover-idle-game-surface-segmented-control-options hover-idle-amplifier-guard-options";
    for (const value of AMPLIFIER_UPGRADE_GUARD_VALUES) {
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "hover-idle-game-surface-segmented-control-option hover-idle-amplifier-guard-option";
      button.dataset.amplifierUpgradeGuardOption = "true";
      button.dataset.virtualPointer = "allow";
      button.dataset.guardMs = String(value);
      button.setAttribute("role", "radio");
      button.textContent = resolveAmplifierUpgradeGuardLabel(value);
      const handler = (): void => {
        this.onAmplifierUpgradeGuardMsChange?.(value);
      };
      button.addEventListener("pointerenter", handler);
      button.addEventListener("pointerdown", handler);
      button.addEventListener("focus", handler);
      button.addEventListener("click", handler);
      this.cleanups.push(() => {
        button.removeEventListener("pointerenter", handler);
        button.removeEventListener("pointerdown", handler);
        button.removeEventListener("focus", handler);
        button.removeEventListener("click", handler);
      });
      this.guardButtons.set(value, button);
      guardOptions.append(button);
    }
    this.guardRoot.append(guardLabel, guardOptions);
    settingsLayer.append(this.guardRoot);

    const scrollContent = document.createElement("div");
    scrollContent.className =
      "hover-idle-game-surface-scroll hover-idle-amplifier-scroll";

    const summary = document.createElement("div");
    summary.className = "hover-idle-amplifier-summary";

    const apStat = document.createElement("div");
    apStat.className = "hover-idle-amplifier-stat";
    this.apValue = document.createElement("span");
    this.apValue.className = "hover-idle-amplifier-stat-value";
    this.apValue.dataset.amplifierValue = "ap";
    apStat.append(this.apValue);

    const hgStat = document.createElement("div");
    hgStat.className = "hover-idle-amplifier-stat";
    this.hgFormula = document.createElement("span");
    this.hgFormula.className = "hover-idle-amplifier-stat-label";
    this.hgFormula.dataset.amplifierFormula = "hg";
    renderHgFormula(this.hgFormula, {
      kind: "sqrt",
      sqrtText: messages.hovView.amplifier.hgFormulaSqrt,
    });
    this.hgMultiplierValue = document.createElement("span");
    this.hgMultiplierValue.className = "hover-idle-amplifier-stat-value";
    this.hgMultiplierValue.dataset.amplifierValue = "hg-multiplier";
    hgStat.append(this.hgFormula, this.hgMultiplierValue);

    summary.append(apStat, hgStat);

    this.entriesRoot = document.createElement("div");
    this.entriesRoot.className = "hover-idle-amplifier-entries";

    scrollContent.append(summary, this.entriesRoot);
    this.root.append(settingsLayer, scrollContent);
  }

  update(
    snapshot: HovAmplifierSnapshot,
    options: HovAmplifierPanelUpdateOptions = {},
  ): void {
    const messages = getMessages();
    this.syncGuardControl(options);
    this.apValue.textContent = snapshot.apLabel;
    renderHgFormula(this.hgFormula, resolveHgFormulaSpec(snapshot, messages));
    this.hgMultiplierValue.textContent = snapshot.hgMultiplierLabel;

    const presentedEntries = snapshot.entries.filter(
      (entry) => entry.playerVisible || entry.corruptedPresentation,
    );
    this.entriesRoot.dataset.entryCount = String(presentedEntries.length);

    const mpPlacement = options.mpPlacement ?? null;
    const upgradeMpPlacement = options.upgradeMpPlacement ?? null;
    const visibleIds = new Set<HovAmplifierEntryId>();
    for (const entry of presentedEntries) {
      const row = this.getOrCreateEntryRow(entry);
      const mpSlot =
        mpPlacement && !entry.corruptedPresentation
          ? resolveAmplifierMpSlotViewState(entry, mpPlacement)
          : null;
      const upgradeMpSlot =
        upgradeMpPlacement && !entry.corruptedPresentation
          ? resolveAmplifierUpgradeMpSlotViewState(entry, upgradeMpPlacement)
          : null;
      row.update(entry, { mpSlot, upgradeMpSlot });
      visibleIds.add(entry.id);
    }

    for (const [id, row] of this.entryRows) {
      if (visibleIds.has(id)) continue;
      row.dispose();
      this.entryRows.delete(id);
    }
  }

  clearHover(): void {
    for (const row of this.entryRows.values()) {
      row.clearHover();
    }
  }

  setInputLocked(locked: boolean): void {
    for (const row of this.entryRows.values()) {
      row.setInputLocked(locked);
    }
  }

  dispose(): void {
    for (const row of this.entryRows.values()) {
      row.dispose();
    }
    this.entryRows.clear();
    for (const cleanup of this.cleanups) {
      cleanup();
    }
    this.cleanups.length = 0;
    this.root.remove();
  }

  private syncGuardControl(options: HovAmplifierPanelUpdateOptions): void {
    const guardMs =
      options.amplifierUpgradeGuardMs ??
      this.getAmplifierUpgradeGuardMs?.() ??
      0;
    this.guardRoot.hidden = !shouldShowAmplifierUpgradeGuardUi({
      amplifierUnlocked: options.amplifierUnlocked ?? false,
      hasSeenAvailableAmplifierUpgradeGuard:
        options.hasSeenAvailableAmplifierUpgradeGuard ?? false,
    });
    for (const [value, button] of this.guardButtons) {
      const active = value === guardMs;
      button.dataset.active = active ? "true" : "false";
      button.setAttribute("aria-checked", active ? "true" : "false");
    }
  }

  private getOrCreateEntryRow(
    entry: HovAmplifierEntrySnapshot,
  ): HovAmplifierEntryRowView {
    const existing = this.entryRows.get(entry.id);
    if (existing) return existing;

    return this.createAndAppendEntryRow(entry);
  }

  private createAndAppendEntryRow(
    entry: HovAmplifierEntrySnapshot,
  ): HovAmplifierEntryRowView {
    const row = new HovAmplifierEntryRowView(entry, {
      pointerModel: this.pointerModel,
      onAmplifyHoverChange: this.onAmplifyEntryHoverChange,
      onPurchaseHovAmplifierGainUpgrade: this.onPurchaseHovAmplifierGainUpgrade,
      getAmplifierUpgradeGuardMs: this.getAmplifierUpgradeGuardMs,
      onAmplifierPanelMpAssign: this.onAmplifierPanelMpAssign,
      onAmplifierPanelMpRemove: this.onAmplifierPanelMpRemove,
      onAmplifierUpgradeMpAssign: this.onAmplifierUpgradeMpAssign,
      onAmplifierUpgradeMpRemove: this.onAmplifierUpgradeMpRemove,
      getHoverToggleDurationMs: this.getHoverToggleDurationMs,
      upgradeRepeatInitialIntervalMs: this.upgradeRepeatInitialIntervalMs,
      speedMultiplier: this.speedMultiplier,
    });
    this.entryRows.set(entry.id, row);
    this.entriesRoot.append(row.root);
    return row;
  }
}

function resolveAmplifierMpSlotViewState(
  entry: HovAmplifierEntrySnapshot,
  mp: HovAmplifierPanelMpState,
): DirectMpTargetViewState {
  const assigned = mp.assignedEntryIds.has(entry.id);
  return resolveDirectMpTargetViewState({
    assigned,
    targetActive: mp.sectionUnlocked && entry.unlocked && entry.mpAssignable,
    sectionEnabled: mp.sectionEnabled,
    canAssign: mp.availableMp > 0,
    visualWhenInactive: "locked",
  });
}

function resolveAmplifierUpgradeMpSlotViewState(
  entry: HovAmplifierEntrySnapshot,
  mp: HovAmplifierUpgradeMpState,
): DirectMpTargetViewState {
  const assigned = mp.assignedEntryIds.has(entry.id);
  return resolveDirectMpTargetViewState({
    assigned,
    targetActive: mp.sectionUnlocked && entry.unlocked && entry.mpAssignable,
    sectionEnabled: mp.sectionEnabled,
    canAssign: mp.availableMp > 0,
    visualWhenInactive: "locked",
  });
}

interface HovAmplifierEntryRowViewOptions {
  readonly pointerModel: HoverPointerModel | null;
  readonly onAmplifyHoverChange:
    | ((entryId: HovAmplifierEntryId, isHovering: boolean) => void)
    | null;
  readonly onPurchaseHovAmplifierGainUpgrade:
    | ((entryId: HovAmplifierEntryId) => boolean)
    | null;
  readonly getAmplifierUpgradeGuardMs: (() => AmplifierUpgradeGuardMs) | null;
  readonly onAmplifierPanelMpAssign:
    | ((entryId: HovAmplifierEntryId) => void)
    | null;
  readonly onAmplifierPanelMpRemove:
    | ((entryId: HovAmplifierEntryId) => void)
    | null;
  readonly onAmplifierUpgradeMpAssign:
    | ((entryId: HovAmplifierEntryId) => void)
    | null;
  readonly onAmplifierUpgradeMpRemove:
    | ((entryId: HovAmplifierEntryId) => void)
    | null;
  readonly getHoverToggleDurationMs: (() => number) | null;
  readonly upgradeRepeatInitialIntervalMs:
    | UpgradeRepeatInitialIntervalSource
    | undefined;
  readonly speedMultiplier: UpgradeRepeatSpeedMultiplierSource | undefined;
}

interface HovAmplifierEntryRowUpdateOptions {
  readonly mpSlot: DirectMpTargetViewState | null;
  readonly upgradeMpSlot: DirectMpTargetViewState | null;
}

interface HovAmplifierMpSlotNodes {
  readonly element: HTMLDivElement;
  readonly controller: MpSlotChargeController;
}

class HovAmplifierEntryRowView implements Disposable {
  readonly root: HTMLDivElement;
  private readonly entryId: HovAmplifierEntryId;
  private readonly upgradeAction: HTMLDivElement;
  private readonly upgradeButton: HTMLButtonElement;
  private readonly upgradeCostLabel: HTMLSpanElement;
  private readonly upgradeGlass: HTMLSpanElement;
  private readonly descriptionCard: HTMLDivElement;
  private readonly shortLabelEl: HTMLSpanElement;
  private readonly tooltip: UpgradeTooltipElements;
  private readonly apparatus: HTMLDivElement;
  private readonly amplifyPanel: HTMLDivElement;
  private readonly pressurePlateLock: HTMLSpanElement;
  private readonly corruptedCharge: HTMLSpanElement;
  private readonly connector: HTMLDivElement;
  private readonly remainingValue: HTMLElement;
  private readonly gauge: HTMLDivElement;
  private readonly gaugeTrack: HTMLDivElement;
  private readonly gaugeFill: HTMLDivElement;
  private readonly gaugeSegments: HTMLDivElement;
  private readonly gaugeSparks: HTMLSpanElement;
  private readonly gaugeOutputLabel: HTMLSpanElement;
  private readonly gaugeOutputValue: HTMLSpanElement;
  private readonly gaugeOutputUnit: HTMLSpanElement;
  private readonly upgradeInteraction: HovAmplifierUpgradeInteractionController | null;
  private readonly panelMpSlot: HovAmplifierMpSlotNodes | null;
  private readonly upgradeMpSlot: HovAmplifierMpSlotNodes | null;
  private readonly cleanups: Array<() => void> = [];
  private readonly onAmplifyHoverChange:
    | ((entryId: HovAmplifierEntryId, isHovering: boolean) => void)
    | null;
  private readonly hoverIntent: HoverIntentController | null;
  private currentEntry: HovAmplifierEntrySnapshot;
  private isUnlocked: boolean;
  private isHoveringActive = false;
  private isDirectMpActive = false;
  private lastGaugeFillPercent = "";
  private lastCanPurchaseUpgrade: boolean | null = null;
  private lastEntryLabel = "";
  private lastGaugeOutputText = "";
  private lastGaugeOutputLabelWidth: GaugeOutputLabelWidth | null = null;
  private lastGaugeOutputHidden: boolean | null = null;
  private lastVisualSegmentsSignature = "";
  private lastSnapshotUpgradeState: AmplifierSnapshotUpgradeState | null = null;

  constructor(
    entry: HovAmplifierEntrySnapshot,
    options: HovAmplifierEntryRowViewOptions = {
      pointerModel: null,
      onAmplifyHoverChange: null,
      onPurchaseHovAmplifierGainUpgrade: null,
      getAmplifierUpgradeGuardMs: null,
      onAmplifierPanelMpAssign: null,
      onAmplifierPanelMpRemove: null,
      onAmplifierUpgradeMpAssign: null,
      onAmplifierUpgradeMpRemove: null,
      getHoverToggleDurationMs: null,
      upgradeRepeatInitialIntervalMs: undefined,
      speedMultiplier: undefined,
    },
  ) {
    const messages = getMessages();
    this.entryId = entry.id;
    this.currentEntry = entry;
    this.isUnlocked = entry.unlocked;
    this.onAmplifyHoverChange = options.onAmplifyHoverChange;
    this.root = document.createElement("div");
    this.root.className =
      "hover-idle-amplifier-entry hover-idle-amplifier-entry-row";
    this.root.dataset.amplifierEntryRow = "true";

    this.upgradeAction = document.createElement("div");
    this.upgradeAction.className = "hover-idle-amplifier-upgrade-action";
    this.upgradeAction.dataset.amplifierUpgradeAction = "true";

    this.upgradeButton = document.createElement("button");
    this.upgradeButton.type = "button";
    this.upgradeButton.className = "hover-idle-amplifier-upgrade-button";
    this.upgradeButton.dataset.amplifierUpgradeButton = "true";
    this.upgradeButton.dataset.virtualPointer = "allow";
    this.upgradeButton.setAttribute("aria-disabled", "true");
    this.upgradeGlass = document.createElement("span");
    this.upgradeGlass.className = "hover-idle-amplifier-upgrade-glass";
    this.upgradeGlass.dataset.amplifierUpgradeGlass = "true";
    this.upgradeGlass.setAttribute("aria-hidden", "true");
    const upgradeContent = document.createElement("span");
    upgradeContent.className = "hover-idle-amplifier-upgrade-content";
    upgradeContent.dataset.amplifierUpgradeContent = "true";
    this.upgradeCostLabel = document.createElement("span");
    this.upgradeCostLabel.className = "hover-idle-amplifier-upgrade-cost";
    this.upgradeCostLabel.dataset.amplifierUpgradeCost = "true";
    this.upgradeCostLabel.textContent =
      messages.hovView.amplifier.upgradeCostNone;
    upgradeContent.append(this.upgradeCostLabel);
    this.upgradeButton.append(this.upgradeGlass, upgradeContent);
    this.upgradeAction.append(this.upgradeButton);

    this.descriptionCard = document.createElement("div");
    this.descriptionCard.className =
      "hover-idle-upgrade-card hover-idle-amplifier-upgrade-card";
    this.descriptionCard.dataset.amplifierUpgradeCard = "true";
    this.descriptionCard.dataset.amplifierCardTooltipAnchor = "true";
    this.descriptionCard.dataset.amplifierGainUpgradeTooltipAnchor = "true";
    this.descriptionCard.dataset.virtualPointer = "allow";
    this.descriptionCard.tabIndex = 0;

    this.shortLabelEl = document.createElement("span");
    this.shortLabelEl.className = "hover-idle-amplifier-short-label";
    this.shortLabelEl.dataset.amplifierShortLabel = "true";
    this.shortLabelEl.dataset.amplifierGainUpgradeShortLabel = "true";

    const tooltipId = `hover-idle-amplifier-card-tooltip-${entry.id}`;
    this.descriptionCard.setAttribute("aria-describedby", tooltipId);
    this.tooltip = createUpgradeTooltip({
      id: tooltipId,
      title: resolveGainUpgradeTitle(entry.id),
      region: messages.hovView.region,
      summary: resolveGainUpgradeSummary(entry.id),
      afterLabel: messages.hovView.amplifier.gainUpgradeTooltip.afterLabel(
        entry.gainUpgradeShortLabel,
      ),
    });
    this.tooltip.root.dataset.amplifierGainUpgradeTooltip = "true";
    this.tooltip.root.dataset.amplifierColor = entry.colorKey;
    this.tooltip.root.classList.add(
      "hover-idle-amplifier-gain-upgrade-tooltip",
    );

    this.descriptionCard.append(this.shortLabelEl);

    this.amplifyPanel = document.createElement("div");
    this.amplifyPanel.className =
      "hover-idle-amplifier-amplify-panel hover-idle-amplifier-pressure-plate";
    this.amplifyPanel.dataset.amplifierAmplifyPanel = "true";
    this.amplifyPanel.dataset.amplifierPressurePlate = "true";
    this.amplifyPanel.dataset.virtualPointer = "allow";

    for (const corner of PRESSURE_PLATE_SCREW_CORNERS) {
      const screw = document.createElement("span");
      screw.className = "hover-idle-amplifier-pressure-plate-screw";
      screw.dataset.amplifierPressurePlateScrew = "true";
      screw.dataset.corner = corner;
      screw.setAttribute("aria-hidden", "true");
      this.amplifyPanel.append(screw);
    }

    this.pressurePlateLock = document.createElement("span");
    this.pressurePlateLock.className =
      "hover-idle-amplifier-pressure-plate-lock";
    this.pressurePlateLock.dataset.amplifierPressurePlateLock = "true";
    this.pressurePlateLock.setAttribute("aria-hidden", "true");
    this.pressurePlateLock.textContent = messages.hoverTarget.lockBadge;
    this.pressurePlateLock.hidden = true;
    this.amplifyPanel.append(this.pressurePlateLock);

    this.corruptedCharge = document.createElement("span");
    this.corruptedCharge.className = "hover-idle-amplifier-corrupted-charge";
    this.corruptedCharge.dataset.amplifierCorruptedCharge = "true";
    this.corruptedCharge.setAttribute("aria-hidden", "true");
    this.amplifyPanel.append(this.corruptedCharge);

    this.connector = document.createElement("div");
    this.connector.className = "hover-idle-amplifier-connector";
    this.connector.dataset.amplifierConnector = "true";
    this.connector.setAttribute("aria-hidden", "true");
    for (const line of AMPLIFIER_CONNECTOR_LINES) {
      const lineEl = document.createElement("span");
      lineEl.className = "hover-idle-amplifier-connector-line";
      lineEl.dataset.amplifierConnectorLine = line;
      this.connector.append(lineEl);
    }

    this.gauge = document.createElement("div");
    this.gauge.className =
      "hover-idle-amplifier-gauge hover-idle-amplifier-gauge-chamber";
    this.gauge.dataset.amplifierGauge = "true";
    this.gauge.dataset.amplifierGaugeChamber = "true";

    this.gaugeTrack = document.createElement("div");
    this.gaugeTrack.className = "hover-idle-amplifier-gauge-track";
    this.gaugeTrack.setAttribute("role", "progressbar");
    this.gaugeTrack.setAttribute("aria-valuemin", "0");
    this.gaugeTrack.setAttribute("aria-valuemax", "100");

    this.gaugeFill = document.createElement("div");
    this.gaugeFill.className = "hover-idle-amplifier-gauge-fill";
    this.gaugeFill.dataset.amplifierGaugeFill = "true";
    this.gaugeSegments = document.createElement("div");
    this.gaugeSegments.className = "hover-idle-amplifier-gauge-segments";
    this.gaugeSegments.dataset.amplifierGaugeSegments = "true";
    this.gaugeSegments.setAttribute("aria-hidden", "true");
    this.gaugeFill.append(this.gaugeSegments);

    this.gaugeOutputLabel = document.createElement("span");
    this.gaugeOutputLabel.className = "hover-idle-amplifier-gauge-output-label";
    this.gaugeOutputLabel.dataset.amplifierGaugeOutputLabel = "true";
    this.gaugeOutputLabel.dataset.amplifierOutputLabelSlot = "true";
    this.gaugeOutputLabel.dataset.outputLabelCovered = "false";
    this.gaugeOutputLabel.setAttribute("aria-hidden", "true");
    this.gaugeOutputLabel.hidden = !entry.unlocked;
    this.gaugeOutputValue = document.createElement("span");
    this.gaugeOutputValue.className = "hover-idle-amplifier-gauge-output-value";
    this.gaugeOutputValue.dataset.amplifierGaugeOutputValue = "true";
    this.gaugeOutputUnit = document.createElement("span");
    this.gaugeOutputUnit.className = "hover-idle-amplifier-gauge-output-unit";
    this.gaugeOutputUnit.dataset.amplifierGaugeOutputUnit = "true";
    const initialGaugeOutputParts = formatHovAmplifierGaugeOutputParts(entry);
    this.gaugeOutputValue.textContent = initialGaugeOutputParts.value;
    this.gaugeOutputUnit.textContent = initialGaugeOutputParts.unit;
    this.gaugeOutputLabel.append(this.gaugeOutputValue, this.gaugeOutputUnit);
    this.gaugeOutputLabel.dataset.labelWidth = resolveGaugeOutputLabelWidth(
      initialGaugeOutputParts.value,
      initialGaugeOutputParts.unit,
    );

    this.gaugeSparks = document.createElement("span");
    this.gaugeSparks.className = "hover-idle-amplifier-gauge-sparks";
    this.gaugeSparks.dataset.amplifierGaugeSparks = "true";
    this.gaugeSparks.setAttribute("aria-hidden", "true");
    for (let i = 0; i < GAUGE_SPARK_COUNT; i += 1) {
      const spark = document.createElement("span");
      spark.dataset.sparkIndex = String(i);
      this.gaugeSparks.append(spark);
    }

    this.gaugeTrack.append(
      this.gaugeFill,
      this.gaugeOutputLabel,
      this.gaugeSparks,
    );

    this.remainingValue = document.createElement("strong");
    this.remainingValue.className = "hover-idle-amplifier-remaining";
    this.remainingValue.dataset.amplifierRemainingValue = "true";
    this.remainingValue.textContent = "";

    this.gauge.append(this.gaugeTrack, this.remainingValue);

    this.apparatus = document.createElement("div");
    this.apparatus.className = "hover-idle-amplifier-apparatus";
    this.apparatus.dataset.amplifierApparatus = "true";
    this.apparatus.append(this.amplifyPanel, this.connector, this.gauge);

    this.root.append(
      this.upgradeAction,
      this.descriptionCard,
      this.apparatus,
      this.tooltip.root,
    );

    if (options.pointerModel !== null && this.onAmplifyHoverChange !== null) {
      this.hoverIntent = new HoverIntentController({
        target: this.amplifyPanel,
        pointerModel: options.pointerModel,
        onHoverChange: (isHovering) => this.applyHoverState(isHovering),
      });
    } else {
      this.hoverIntent = null;
    }

    if (
      options.pointerModel !== null &&
      options.onPurchaseHovAmplifierGainUpgrade !== null
    ) {
      this.upgradeInteraction = createHovAmplifierUpgradeInteractionController({
        tooltipAnchor: this.descriptionCard,
        purchaseButton: this.upgradeButton,
        tooltipRoot: this.tooltip.root,
        pointerModel: options.pointerModel,
        getEntrySnapshot: () => this.currentEntry,
        onPurchase: (snapshot) =>
          options.onPurchaseHovAmplifierGainUpgrade?.(snapshot.id) ?? false,
        getGuardMs: options.getAmplifierUpgradeGuardMs ?? (() => 0),
        initialIntervalMs: options.upgradeRepeatInitialIntervalMs,
        speedMultiplier: options.speedMultiplier,
      });
    } else {
      this.upgradeInteraction = null;
    }

    if (
      options.pointerModel !== null &&
      options.onAmplifierPanelMpAssign !== null &&
      options.onAmplifierPanelMpRemove !== null
    ) {
      this.panelMpSlot = this.createMpSlot({
        entryId: entry.id,
        targetKind: "amplifier-panel",
        pointerModel: options.pointerModel,
        onAssign: options.onAmplifierPanelMpAssign,
        onRemove: options.onAmplifierPanelMpRemove,
        getHoverToggleDurationMs: options.getHoverToggleDurationMs,
      });
      this.amplifyPanel.append(this.panelMpSlot.element);
    } else {
      this.panelMpSlot = null;
    }

    if (
      options.pointerModel !== null &&
      options.onAmplifierUpgradeMpAssign !== null &&
      options.onAmplifierUpgradeMpRemove !== null
    ) {
      this.upgradeMpSlot = this.createMpSlot({
        entryId: entry.id,
        targetKind: "amplifier-upgrade",
        pointerModel: options.pointerModel,
        onAssign: options.onAmplifierUpgradeMpAssign,
        onRemove: options.onAmplifierUpgradeMpRemove,
        getHoverToggleDurationMs: options.getHoverToggleDurationMs,
      });
      this.upgradeAction.append(this.upgradeMpSlot.element);
    } else {
      this.upgradeMpSlot = null;
    }

    this.update(entry);
  }

  private createMpSlot(input: {
    readonly entryId: HovAmplifierEntryId;
    readonly targetKind: "amplifier-panel" | "amplifier-upgrade";
    readonly pointerModel: HoverPointerModel;
    readonly onAssign: (entryId: HovAmplifierEntryId) => void;
    readonly onRemove: (entryId: HovAmplifierEntryId) => void;
    readonly getHoverToggleDurationMs: (() => number) | null;
  }): HovAmplifierMpSlotNodes {
    const element = document.createElement("div");
    element.className = "hover-idle-mp-slot hover-idle-mp-slot--target-corner";
    if (input.targetKind === "amplifier-panel") {
      element.dataset.amplifierPanelMpSlot = "true";
    } else {
      element.dataset.amplifierUpgradeMpSlot = "true";
    }
    element.dataset.amplifierEntryId = input.entryId;
    element.dataset.mpTargetKind = input.targetKind;
    element.dataset.mpTargetKey = `${input.targetKind}:${input.entryId}`;
    element.dataset.virtualPointer = "allow";
    element.dataset.paused = "false";
    element.hidden = true;
    element.setAttribute("role", "button");
    element.tabIndex = 0;
    const amplifierMessages = getMessages().hovView.amplifier;
    const entryLabel = amplifierMessages.entries[input.entryId];
    element.setAttribute(
      "aria-label",
      input.targetKind === "amplifier-panel"
        ? amplifierMessages.panelMpSlotAriaLabel(entryLabel)
        : amplifierMessages.upgradeMpSlotAriaLabel(entryLabel),
    );

    const fill = document.createElement("span");
    fill.className = "hover-idle-mp-slot-fill";
    fill.setAttribute("aria-hidden", "true");

    const icon = document.createElement("span");
    icon.className = "hover-idle-mp-slot-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = MOUSE_POINTER_ICON_SVG_SOURCE;

    const pauseOverlay = document.createElement("span");
    pauseOverlay.className = "hover-idle-mp-slot-pause-overlay";
    pauseOverlay.setAttribute("aria-hidden", "true");
    pauseOverlay.innerHTML = pauseIconSource;

    element.append(fill, icon, pauseOverlay);

    const durationGetter = input.getHoverToggleDurationMs;
    const controller = new MpSlotChargeController({
      element,
      pointerModel: input.pointerModel,
      getChargeMs: durationGetter ? () => durationGetter() : undefined,
      onAssign: () => input.onAssign(input.entryId),
      onRemove: () => input.onRemove(input.entryId),
    });
    return { element, controller };
  }

  clearHover(): void {
    this.hoverIntent?.clearPointerHover();
    this.upgradeInteraction?.clearHover();
    this.panelMpSlot?.controller.clearPointerHover();
    this.upgradeMpSlot?.controller.clearPointerHover();
    if (this.isHoveringActive) {
      this.applyHoverState(false);
    }
  }

  setInputLocked(locked: boolean): void {
    this.upgradeInteraction?.setInputLocked(locked);
    this.panelMpSlot?.controller.setInputLocked(locked);
    this.upgradeMpSlot?.controller.setInputLocked(locked);
    if (locked) {
      this.hoverIntent?.clearPointerHover();
      if (this.isHoveringActive) {
        this.applyHoverState(false);
      }
      return;
    }
    this.hoverIntent?.refresh({ forceEmit: true });
    this.upgradeInteraction?.refresh();
  }

  private applyHoverState(isHovering: boolean): void {
    if (this.currentEntry.corruptedPresentation) {
      if (this.isHoveringActive) {
        this.isHoveringActive = false;
        this.onAmplifyHoverChange?.(this.entryId, false);
      }
      this.syncHoverDataset();
      return;
    }
    if (this.isHoveringActive === isHovering) return;
    this.isHoveringActive = isHovering;
    this.syncHoverDataset();
    this.onAmplifyHoverChange?.(this.entryId, isHovering);
  }

  private syncHoverDataset(): void {
    const visuallyActive =
      (this.isHoveringActive || this.isDirectMpActive) &&
      this.isUnlocked &&
      !this.currentEntry.corruptedPresentation;
    if (visuallyActive) {
      this.amplifyPanel.dataset.hovering = "true";
      this.root.dataset.charging = "true";
    } else {
      delete this.amplifyPanel.dataset.hovering;
      delete this.root.dataset.charging;
    }
  }

  update(
    entry: HovAmplifierEntrySnapshot,
    updateOptions: HovAmplifierEntryRowUpdateOptions = {
      mpSlot: null,
      upgradeMpSlot: null,
    },
  ): void {
    this.currentEntry = entry;
    const messages = getMessages();
    const entryLabel = resolveHovAmplifierEntryLabel(entry.id);
    const state = resolveEntryState(entry);
    if (this.isUnlocked !== entry.unlocked) {
      this.isUnlocked = entry.unlocked;
      if (this.isHoveringActive || this.isDirectMpActive) {
        this.syncHoverDataset();
      }
    }
    if (entry.corruptedPresentation && this.isHoveringActive) {
      this.isHoveringActive = false;
      this.onAmplifyHoverChange?.(this.entryId, false);
      this.syncHoverDataset();
    }
    syncEntryDataset(this.root, entry, state);
    syncEntryDataset(this.upgradeButton, entry, state);
    syncEntryDataset(this.descriptionCard, entry, state);
    syncEntryDataset(this.apparatus, entry, state);
    syncEntryDataset(this.amplifyPanel, entry, state);
    syncEntryDataset(this.connector, entry, state);
    syncEntryDataset(this.gauge, entry, state);
    setDatasetValue(this.amplifyPanel, "amplifyEntryId", entry.id);
    setDatasetValue(this.gauge, "gaugeState", entry.burnState);
    setDatasetValue(this.gaugeFill, "burnState", entry.burnState);
    setOptionalDataset(
      this.gauge,
      "consumingVisualSource",
      entry.consumingVisualSource,
    );
    setOptionalDataset(
      this.gaugeSparks,
      "consumingVisualSource",
      entry.consumingVisualSource,
    );
    setTextContentIfChanged(
      this.pressurePlateLock,
      entry.corruptedPresentation
        ? messages.hovView.amplifier.corruptedLabel
        : messages.hoverTarget.lockBadge,
    );
    setHiddenIfChanged(
      this.pressurePlateLock,
      entry.unlocked && !entry.corruptedPresentation,
    );
    this.gauge.setAttribute(
      "aria-hidden",
      entry.corruptedPresentation ? "true" : "false",
    );
    this.descriptionCard.tabIndex = entry.corruptedPresentation ? -1 : 0;
    this.upgradeButton.disabled = entry.corruptedPresentation;

    if (this.lastEntryLabel !== entryLabel) {
      this.lastEntryLabel = entryLabel;
      this.upgradeButton.setAttribute(
        "aria-label",
        messages.hovView.amplifier.upgradeButtonAriaLabel(entryLabel),
      );
      this.gaugeTrack.setAttribute(
        "aria-label",
        `${entryLabel} ${messages.hovView.amplifier.gaugeLabel}`,
      );
    }
    this.amplifyPanel.setAttribute(
      "aria-label",
      entry.corruptedPresentation
        ? messages.hovView.amplifier.corruptedPanelAriaLabel(entryLabel)
        : messages.hovView.amplifier.amplifyPanelAriaLabel(entryLabel),
    );

    if (this.lastCanPurchaseUpgrade !== entry.canPurchaseUpgrade) {
      this.lastCanPurchaseUpgrade = entry.canPurchaseUpgrade;
      this.upgradeButton.setAttribute(
        "aria-disabled",
        entry.canPurchaseUpgrade ? "false" : "true",
      );
      setDatasetValue(
        this.upgradeButton,
        "affordable",
        entry.canPurchaseUpgrade ? "true" : "false",
      );
    }
    const snapshotUpgradeState = computeAmplifierSnapshotUpgradeState(entry);
    if (this.lastSnapshotUpgradeState !== snapshotUpgradeState) {
      this.lastSnapshotUpgradeState = snapshotUpgradeState;
      setDatasetValue(this.upgradeButton, "upgradeState", snapshotUpgradeState);
    }
    this.upgradeInteraction?.refresh();
    setTextContentIfChanged(
      this.upgradeCostLabel,
      messages.hovView.amplifier.upgradeCost(
        formatForContext(entry.upgradeCost, "compact"),
      ),
    );
    setTextContentIfChanged(this.shortLabelEl, entry.gainUpgradeShortLabel);
    this.applyTooltipValues(entry);

    const remainingTime = resolveHovAmplifierRemainingTimeText(entry);
    setTextContentIfChanged(this.remainingValue, remainingTime);

    const gaugeOutputParts = formatHovAmplifierGaugeOutputParts(entry);
    const gaugeOutputText = formatHovAmplifierGaugeOutputText(gaugeOutputParts);
    if (this.lastGaugeOutputText !== gaugeOutputText) {
      this.lastGaugeOutputText = gaugeOutputText;
      setTextContentIfChanged(this.gaugeOutputValue, gaugeOutputParts.value);
      setTextContentIfChanged(this.gaugeOutputUnit, gaugeOutputParts.unit);
    }
    const gaugeOutputLabelWidth = resolveGaugeOutputLabelWidth(
      gaugeOutputParts.value,
      gaugeOutputParts.unit,
    );
    if (this.lastGaugeOutputLabelWidth !== gaugeOutputLabelWidth) {
      this.lastGaugeOutputLabelWidth = gaugeOutputLabelWidth;
      setDatasetValue(
        this.gaugeOutputLabel,
        "labelWidth",
        gaugeOutputLabelWidth,
      );
    }
    const gaugeOutputHidden = !entry.unlocked || entry.corruptedPresentation;
    if (this.lastGaugeOutputHidden !== gaugeOutputHidden) {
      this.lastGaugeOutputHidden = gaugeOutputHidden;
      this.gaugeOutputLabel.hidden = gaugeOutputHidden;
    }

    const gaugePercent = resolveGaugeFillPercent(entry.gaugeValue);
    const gaugePercentText = formatGaugePercent(gaugePercent);
    const gaugeFillPercent = `${gaugePercentText}%`;

    if (this.lastGaugeFillPercent !== gaugeFillPercent) {
      this.lastGaugeFillPercent = gaugeFillPercent;
      this.gaugeTrack.setAttribute("aria-valuenow", gaugePercentText);
      this.gaugeTrack.style.setProperty(
        "--hov-amplifier-gauge-fill-percent",
        gaugeFillPercent,
      );
      this.gaugeFill.style.width = gaugeFillPercent;
    }
    this.syncVisualSegments(entry.visualSegments, entry.gaugeValue);
    this.syncPanelMpSlot(updateOptions.mpSlot);
    this.syncMpSlot(this.upgradeMpSlot, updateOptions.upgradeMpSlot);
  }

  private syncPanelMpSlot(slot: DirectMpTargetViewState | null): void {
    const nextDirectMpActive = slot !== null && slot.effective;
    if (this.isDirectMpActive !== nextDirectMpActive) {
      this.isDirectMpActive = nextDirectMpActive;
      this.syncHoverDataset();
    }
    this.syncMpSlot(this.panelMpSlot, slot);
  }

  private syncMpSlot(
    nodes: HovAmplifierMpSlotNodes | null,
    slot: DirectMpTargetViewState | null,
  ): void {
    if (nodes === null) return;
    if (slot === null) {
      nodes.element.hidden = true;
      nodes.element.dataset.paused = "false";
      nodes.element.dataset.assigned = "false";
      nodes.element.dataset.disabled = "false";
      nodes.controller.clearPointerHover();
      nodes.controller.sync({
        isAssigned: false,
        canAssign: false,
        canRemove: false,
      });
      return;
    }
    if (!slot.showSlot) {
      nodes.controller.clearPointerHover();
    }
    nodes.element.hidden = !slot.showSlot;
    nodes.element.dataset.paused = slot.showPauseOverlay ? "true" : "false";
    nodes.element.dataset.assigned = slot.assigned ? "true" : "false";
    nodes.element.dataset.disabled =
      !slot.assigned && !slot.canAssign ? "true" : "false";
    nodes.controller.sync({
      isAssigned: slot.assigned,
      canAssign: slot.canAssign,
      canRemove: slot.canRemove,
    });
  }

  dispose(): void {
    this.clearHover();
    this.hoverIntent?.dispose();
    this.upgradeInteraction?.dispose();
    this.panelMpSlot?.controller.dispose();
    this.upgradeMpSlot?.controller.dispose();
    for (const cleanup of this.cleanups) {
      cleanup();
    }
    this.cleanups.length = 0;
    this.root.remove();
  }

  private applyTooltipValues(entry: HovAmplifierEntrySnapshot): void {
    const tooltipMessages = getMessages().hovView.amplifier.gainUpgradeTooltip;
    const currentGain = formatForContext(
      entry.effectiveAmplifierGainPerSecond,
      "tooltip",
    );
    const nextGain = formatForContext(
      entry.effectiveAmplifierGainPerSecondAfterUpgrade,
      "tooltip",
    );
    const delta = formatForContext(
      entry.effectiveAmplifierGainPerSecondAfterUpgrade.sub(
        entry.effectiveAmplifierGainPerSecond,
      ),
      "tooltip",
    );
    setTextContentIfChanged(
      this.tooltip.title,
      resolveGainUpgradeTitle(entry.id),
    );
    setTextContentIfChanged(
      this.tooltip.summary,
      resolveGainUpgradeSummary(entry.id),
    );
    setTextContentIfChanged(
      this.tooltip.effectValue,
      tooltipMessages.effect(delta, entry.gainUpgradeShortLabel),
    );
    setTextContentIfChanged(
      this.tooltip.afterLabel,
      tooltipMessages.afterLabel(entry.gainUpgradeShortLabel),
    );
    setTextContentIfChanged(
      this.tooltip.afterValue,
      tooltipMessages.after(currentGain, nextGain),
    );
    setTextContentIfChanged(
      this.tooltip.costValue,
      tooltipMessages.cost(formatForContext(entry.upgradeCost, "tooltip")),
    );
    setTextContentIfChanged(
      this.tooltip.stateValue,
      entry.canPurchaseUpgrade
        ? tooltipMessages.stateReady
        : tooltipMessages.stateNeed(
            formatForContext(entry.missingHovCoreForUpgrade, "tooltip"),
          ),
    );
    setDatasetValue(
      this.tooltip.root,
      "amplifierUpgradeState",
      entry.canPurchaseUpgrade ? "ready" : "shortage",
    );
    setDatasetValue(
      this.tooltip.root,
      "affordable",
      entry.canPurchaseUpgrade ? "true" : "false",
    );
  }

  private syncVisualSegments(
    segments: readonly HovAmplifierGaugeVisualSegment[],
    gaugeValue: number,
  ): void {
    const signature = createVisualSegmentsSignature(segments, gaugeValue);
    if (this.lastVisualSegmentsSignature === signature) return;
    this.lastVisualSegmentsSignature = signature;
    while (this.gaugeSegments.children.length > 0) {
      this.gaugeSegments.children[0]?.remove();
    }
    const total = segments.reduce((sum, segment) => sum + segment.amount, 0);
    if (!Number.isFinite(total) || total <= 0) {
      clearVisualSegmentsActive(this.gauge);
      clearVisualSegmentsActive(this.gaugeFill);
      return;
    }
    setDatasetValue(this.gauge, "visualSegments", "active");
    setDatasetValue(this.gaugeFill, "visualSegments", "active");
    for (const segment of segments) {
      const segmentEl = document.createElement("span");
      segmentEl.className = "hover-idle-amplifier-gauge-segment";
      segmentEl.dataset.amplifierGaugeSegment = "true";
      segmentEl.dataset.mixed = isMixedVisualSegment(segment)
        ? "true"
        : "false";
      const dominant = resolveDominantVisualSegmentSource(segment);
      setOptionalDataset(segmentEl, "source", dominant);
      segmentEl.style.setProperty(
        "--hov-amplifier-visual-segment-share",
        String(Math.max(0, segment.amount / total)),
      );
      applyVisualSegmentColorProperties(segmentEl, segment);
      this.gaugeSegments.append(segmentEl);
    }
  }
}

const AMPLIFIER_UPGRADE_GUARD_VALUES = [0, 1000, 3000] as const;

const PRESSURE_PLATE_SCREW_CORNERS = ["tl", "tr", "bl", "br"] as const;

const AMPLIFIER_CONNECTOR_LINES = ["top", "middle", "bottom"] as const;

const GAUGE_SPARK_COUNT = 4;

export function shouldShowAmplifierUpgradeGuardUi(options: {
  readonly amplifierUnlocked: boolean;
  readonly hasSeenAvailableAmplifierUpgradeGuard: boolean;
}): boolean {
  return (
    options.amplifierUnlocked && options.hasSeenAvailableAmplifierUpgradeGuard
  );
}

export function shouldUnlockAmplifierUpgradeGuardUi(options: {
  readonly amplifierUnlocked: boolean;
  readonly entries: readonly HovAmplifierEntrySnapshot[];
}): boolean {
  return (
    options.amplifierUnlocked &&
    options.entries.some((entry) => entry.canPurchaseUpgrade)
  );
}

function resolveAmplifierUpgradeGuardLabel(
  value: AmplifierUpgradeGuardMs,
): string {
  return getMessages().hovView.amplifier.guard.options[value];
}

function resolveGainUpgradeTitle(id: HovAmplifierEntryId): string {
  return getMessages().hovView.amplifier.gainUpgradeTooltip.byEntry[id].title;
}

function resolveGainUpgradeSummary(id: HovAmplifierEntryId): string {
  return getMessages().hovView.amplifier.gainUpgradeTooltip.byEntry[id].summary;
}

function syncEntryDataset(
  element: HTMLElement,
  entry: HovAmplifierEntrySnapshot,
  state: "active" | "dormant",
): void {
  setDatasetValue(element, "amplifierEntryId", entry.id);
  setDatasetValue(element, "amplifierColor", entry.colorKey);
  setDatasetValue(element, "colorKey", entry.colorKey);
  setDatasetValue(element, "labelKey", entry.labelKey);
  setDatasetValue(element, "unlocked", entry.unlocked ? "true" : "false");
  setDatasetValue(element, "locked", entry.unlocked ? "false" : "true");
  setDatasetValue(
    element,
    "corrupted",
    entry.corruptedPresentation ? "true" : "false",
  );
  setDatasetValue(element, "state", state);
  setDatasetValue(element, "burnState", entry.burnState);
  setOptionalDataset(
    element,
    "feedsGaugeForEntryId",
    entry.feedsGaugeForEntryId,
  );
  setOptionalDataset(
    element,
    "receivesGaugeFromEntryId",
    entry.receivesGaugeFromEntryId,
  );
  setOptionalDataset(
    element,
    "chargeSource",
    entry.recentChargeSource?.sourceColorKey ?? null,
  );
  setOptionalChargeMode(element, entry.recentChargeMode);
}

function setOptionalChargeMode(
  element: HTMLElement,
  mode: HovAmplifierEntrySnapshot["recentChargeMode"],
): void {
  if (mode === null) {
    if (element.dataset.chargeMode !== undefined) {
      delete element.dataset.chargeMode;
    }
    return;
  }
  setDatasetValue(element, "chargeMode", mode);
}

function clearVisualSegmentsActive(element: HTMLElement): void {
  if (element.dataset.visualSegments !== undefined) {
    delete element.dataset.visualSegments;
  }
}

function setOptionalDataset(
  element: HTMLElement,
  key: string,
  value: HovAmplifierColorKey | HovAmplifierEntryId | null,
): void {
  if (value === null) {
    if (element.dataset[key] !== undefined) {
      delete element.dataset[key];
    }
    return;
  }
  setDatasetValue(element, key, value);
}

function createVisualSegmentsSignature(
  segments: readonly HovAmplifierGaugeVisualSegment[],
  gaugeValue: number,
): string {
  return `${formatGaugePercent(resolveGaugeFillPercent(gaugeValue))}:${segments
    .map(
      (segment) =>
        `${formatGaugePercent(segment.amount * 100)}:${HOV_AMPLIFIER_VISUAL_COLOR_KEYS.map(
          (key) =>
            `${key}:${formatGaugePercent((segment.sources[key] ?? 0) * 100)}`,
        ).join(",")}`,
    )
    .join("|")}`;
}

const HOV_AMPLIFIER_VISUAL_COLOR_KEYS: readonly HovAmplifierColorKey[] = [
  "red",
  "orange",
  "yellow",
  "green",
  "cyan",
  "blue",
  "purple",
  "light",
  "dark",
];

function isMixedVisualSegment(
  segment: HovAmplifierGaugeVisualSegment,
): boolean {
  return (
    HOV_AMPLIFIER_VISUAL_COLOR_KEYS.filter(
      (key) => (segment.sources[key] ?? 0) > 0,
    ).length > 1
  );
}

function applyVisualSegmentColorProperties(
  element: HTMLElement,
  segment: HovAmplifierGaugeVisualSegment,
): void {
  const orderedSources = HOV_AMPLIFIER_VISUAL_COLOR_KEYS.map((key) => ({
    key,
    ratio: segment.sources[key] ?? 0,
  }))
    .filter((source) => source.ratio > 0)
    .sort((left, right) => right.ratio - left.ratio);
  const primary = orderedSources[0]?.key ?? "red";
  const secondary = orderedSources[1]?.key ?? primary;
  element.style.setProperty(
    "--hov-amplifier-visual-segment-color",
    `var(--hov-amplifier-color-${primary})`,
  );
  element.style.setProperty(
    "--hov-amplifier-visual-segment-alt-color",
    `var(--hov-amplifier-color-${secondary})`,
  );
  element.style.setProperty(
    "--hov-amplifier-visual-segment-primary-ratio",
    `${Math.round((orderedSources[0]?.ratio ?? 1) * 100)}%`,
  );
}

function setDatasetValue(
  element: HTMLElement,
  key: string,
  value: string,
): void {
  if (element.dataset[key] !== value) {
    element.dataset[key] = value;
  }
}

function setTextContentIfChanged(element: HTMLElement, value: string): void {
  if (element.textContent !== value) {
    element.textContent = value;
  }
}

function setHiddenIfChanged(element: HTMLElement, hidden: boolean): void {
  if (element.hidden !== hidden) {
    element.hidden = hidden;
  }
}

function resolveEntryState(
  entry: HovAmplifierEntrySnapshot,
): "active" | "dormant" {
  return entry.unlocked ? "active" : "dormant";
}

function resolveGaugeFillPercent(gaugeValue: number): number {
  if (!Number.isFinite(gaugeValue)) return 0;
  return Math.max(0, Math.min(1, gaugeValue)) * 100;
}

function formatGaugePercent(value: number): string {
  const rounded = Math.round(value * 1000) / 1000;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return rounded.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

interface HovAmplifierGaugeOutputParts {
  readonly value: string;
  readonly unit: string;
}

function formatHovAmplifierGaugeOutputParts(
  entry: HovAmplifierEntrySnapshot,
): HovAmplifierGaugeOutputParts {
  const value = formatForContext(entry.activeGaugeOutputGain, "compact");
  return { value, unit: entry.gaugeOutputLabel };
}

function formatHovAmplifierGaugeOutputText(
  parts: HovAmplifierGaugeOutputParts,
): string {
  return `${parts.value} ${parts.unit}`;
}

type GaugeOutputLabelWidth = "short" | "medium" | "long" | "huge";

export function resolveGaugeOutputLabelWidth(
  value: string,
  unit = "",
): GaugeOutputLabelWidth {
  const valueLength = value.length;
  const unitLength = unit.length;
  if (valueLength <= 4 && unitLength <= 3) return "short";
  if (valueLength <= 5 && unitLength <= 4) return "medium";
  if (valueLength <= 7 && unitLength <= 4) return "long";
  return "huge";
}

const HOV_AMPLIFIER_REMAINING_TIME_PLACEHOLDER = "--:--";

export function resolveHovAmplifierRemainingTimeText(
  entry: HovAmplifierEntrySnapshot,
): string {
  if (!entry.unlocked) return "";
  if (entry.remainingSeconds === null) {
    return HOV_AMPLIFIER_REMAINING_TIME_PLACEHOLDER;
  }
  if (entry.burnState === "starved") {
    return HOV_AMPLIFIER_REMAINING_TIME_PLACEHOLDER;
  }
  return formatHovAmplifierRemainingTime(entry.remainingSeconds);
}

function resolveHovAmplifierEntryLabel(id: HovAmplifierEntryId): string {
  return getMessages().hovView.amplifier.entries[id];
}

type HgFormulaSpec =
  | { readonly kind: "sqrt"; readonly sqrtText: string }
  | {
      readonly kind: "exponent";
      readonly baseText: string;
      readonly exponentText: string;
    };

const HG_FORMULA_EXPONENT_SQRT_EPSILON = 1e-9;

function resolveHgFormulaSpec(
  snapshot: HovAmplifierSnapshot,
  messages: ReturnType<typeof getMessages>,
): HgFormulaSpec {
  const breakdown = snapshot.apExponentBreakdown;
  const totalBonus =
    breakdown.apBoarExponentBonus +
    breakdown.apDeerExponentBonus +
    breakdown.apButterflyExponentBonus;
  const isHalfExponent =
    Math.abs(snapshot.effectiveApExponent - 0.5) <
      HG_FORMULA_EXPONENT_SQRT_EPSILON && totalBonus === 0;
  if (isHalfExponent) {
    return { kind: "sqrt", sqrtText: messages.hovView.amplifier.hgFormulaSqrt };
  }
  return {
    kind: "exponent",
    baseText: messages.hovView.amplifier.hgFormulaExponentBase,
    exponentText: formatHgFormulaExponent(snapshot.effectiveApExponent),
  };
}

function formatHgFormulaExponent(exponent: number): string {
  if (!Number.isFinite(exponent)) return "—";
  if (Math.abs(exponent) < 1000) {
    const fixed = exponent.toFixed(2);
    return trimTrailingZeros(fixed);
  }
  return formatForContext(exponent, "compact", getCurrentNotation(), {
    decimals: 2,
    trimTrailingZeros: true,
  });
}

function trimTrailingZeros(value: string): string {
  if (!value.includes(".")) return value;
  return value.replace(/\.?0+$/, "");
}

function renderHgFormula(target: HTMLElement, spec: HgFormulaSpec): void {
  if (spec.kind === "sqrt") {
    if (target.dataset.amplifierFormulaMode === "sqrt") {
      if (target.textContent !== spec.sqrtText) {
        target.textContent = spec.sqrtText;
      }
      return;
    }
    target.dataset.amplifierFormulaMode = "sqrt";
    target.textContent = spec.sqrtText;
    return;
  }
  if (target.dataset.amplifierFormulaMode !== "exponent") {
    target.dataset.amplifierFormulaMode = "exponent";
    target.replaceChildren();
    const baseNode = document.createTextNode(spec.baseText);
    const sup = document.createElement("sup");
    sup.dataset.amplifierFormulaExponent = "true";
    sup.textContent = spec.exponentText;
    target.append(baseNode, sup);
    return;
  }
  const sup = target.querySelector<HTMLElement>(
    "sup[data-amplifier-formula-exponent='true']",
  );
  if (sup === null) {
    target.replaceChildren();
    const baseNode = document.createTextNode(spec.baseText);
    const exponentSup = document.createElement("sup");
    exponentSup.dataset.amplifierFormulaExponent = "true";
    exponentSup.textContent = spec.exponentText;
    target.append(baseNode, exponentSup);
    return;
  }
  if (target.firstChild?.nodeType === Node.TEXT_NODE) {
    const baseNode = target.firstChild as Text;
    if (baseNode.nodeValue !== spec.baseText) {
      baseNode.nodeValue = spec.baseText;
    }
  } else {
    target.insertBefore(document.createTextNode(spec.baseText), sup);
  }
  if (sup.textContent !== spec.exponentText) {
    sup.textContent = spec.exponentText;
  }
}
